import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useIntl} from 'react-intl';
import cn from 'classnames';

import AddressModel from 'coreClasses/order/AddressModel';
import GiftCertificateItemModel from 'coreClasses/order/GiftCertificateItemModel';
import PaymentInstrumentModel from 'coreClasses/order/PaymentInstrumentModel';
import ShipmentModel from 'coreClasses/order/ShipmentModel';
import {useConfiguration, useCustomerMgr, useLocalesMgr} from 'coreHooks';
import {EReturnAuthorizationProvider} from 'coreStore/data/configurations/types';

import {useGeneralProvider} from 'brand/app/components/_app-config/GeneralProvider';
import ShowOnDesktop from 'brandComponents/Responsive/ShowOnDesktop';

import Accordion from '../Accordion';
import OrderStatusWidget from '../OrderStatusWidget';

import {IOrderDetailsProps} from './IOrderDetails';
import messages from './messages';
import {
	OrderDetailsAccordionHeading,
	OrderDetailsBottomSection,
	OrderDetailsGiftCardItem,
	OrderDetailsItem,
	OrderDetailsSummary,
	OrderDetailsTopSection,
} from './Subcomponents';

import styles from './OrderDetails.module.scss';

const OrderDetails = ({order, onReturnOrderSelected, isAccordion, isTrackOrder}: IOrderDetailsProps) => {
	const intl = useIntl();
	const {managerRepository} = useGeneralProvider();
	const CustomerMgr = useCustomerMgr();
	const customer = CustomerMgr.getCurrentCustomer();
	const returnAuthorizationProvider = useConfiguration('sitePreferences.returnAuthProvider');
	const isReturnAppProvider = returnAuthorizationProvider === EReturnAuthorizationProvider.RETURN_APP;
	const shipment: ShipmentModel = order.getShipments()?.[0] || null;
	const shippingAddress: AddressModel = shipment?.getShippingAddress() || null;
	const billingAddress: AddressModel = order.getBillingAddress();
	const paymentInstruments: PaymentInstrumentModel[] = order.getPaymentInstruments();
	const productItems = order.getAllItems();
	const orderNumber = order.getOrderNumber();
	const currency = order?.getCurrency();
	const orderTotal = order?.getOrderTotal();
	const localesMgr = useLocalesMgr();
	const currentLanguage = localesMgr.getCurrentLocale().getLanguageCode();
	const productTotal = order?.getProductTotal();
	const orderReturnLink = order.getReturnLink();
	const [returnLink, setReturnLink] = useState<string>(orderReturnLink);
	const isLoadingReturnLink = useRef(false);

	const localizedReturnLink = useMemo(() => {
		if (!returnLink) {
			return returnLink;
		}

		try {
			const parsed = new URL(returnLink);
			const segments = parsed.pathname.split('/').filter(Boolean);
			const [firstSegment] = segments;

			if (firstSegment === currentLanguage) {
				return returnLink;
			}

			if (firstSegment && /^[a-z]{2}$/.test(firstSegment)) {
				segments[0] = currentLanguage;
			} else {
				segments.unshift(currentLanguage);
			}

			parsed.pathname = `/${segments.join('/')}`;

			return parsed.toString();
		} catch {
			return returnLink;
		}
	}, [returnLink, currentLanguage]);

	const fetchReturnLink = useCallback(async () => {
		isLoadingReturnLink.current = true;

		try {
			const res = await managerRepository.getOrdersMgr().getReturnLink(orderNumber);

			if (res && res !== '') {
				setReturnLink(res);
			}
		} catch (error) {
			console.error('Failed to fetch return link:', error);
		} finally {
			isLoadingReturnLink.current = false;
		}
	}, [orderNumber]);

	useEffect(() => {
		if (!returnLink && isReturnAppProvider && orderNumber && customer?.isFirstAuthComplete && !isLoadingReturnLink.current) {
			fetchReturnLink();
		}
	}, [returnLink, orderReturnLink, orderNumber, isReturnAppProvider, customer?.isFirstAuthComplete]);

	let shippingCost = shipment?.getShippingCost() ?? shipment?.getShippingTotal();

	if (isTrackOrder && orderTotal === productTotal) {
		shippingCost = 0;
	}

	const returnAuthorizationStatusLabel = order.getReturnAuthorizationStatus()
		? intl.formatMessage(messages.returnRequested)
		: '-';

	const isReturnable = order.isReturnable() && (!order.getReturnAuthorizationStatus() || isReturnAppProvider);

	const handleReturnOrder = useCallback(() => {
		if (!isReturnAppProvider) {
			onReturnOrderSelected?.(order);
		}
	}, [isReturnAppProvider, order, onReturnOrderSelected]);

	const totalQuantityItems = useMemo(() => {
		return productItems?.reduce((a, c) => a + c.getQuantity(), 0);
	}, [productItems]);

	const renderOrderDetailsBody = () => {
		return (
			<div>
				<ShowOnDesktop>{order.getProductItems() && <OrderStatusWidget order={order} />}</ShowOnDesktop>
				<OrderDetailsTopSection
					classNames={{
						wrap: styles['order-details__top-section'],
						buttonWrap: styles['order-details__top-section__button-wrapper'],
					}}
					productsNumber={totalQuantityItems}
					orderNumber={orderNumber}
					isReturnable={isReturnable}
					trackingUrl={order.getTrackingUrl()}
					returnAuthorizationStatusLabel={returnAuthorizationStatusLabel}
					handleReturnOrder={handleReturnOrder}
					returnLink={isReturnAppProvider && localizedReturnLink ? localizedReturnLink : undefined}
				/>
				<div className={cn(styles['order-details__items'], 'order-details__items')}>
					{productItems?.length &&
						productItems.map((item) => {
							if (item instanceof GiftCertificateItemModel) {
								return (
									<OrderDetailsGiftCardItem
										key={`product-${item.getItemId()}`}
										GiftCardItem={item}
										currency={currency}
										classNames={{
											item: styles['order-details__item'],
										}}
									/>
								);
							} else {
								return (
									<OrderDetailsItem
										key={`product-${item.getItemId()}`}
										productItem={item}
										currency={currency}
										shippingCountryCode={shippingAddress?.getCountryCode()}
										classNames={{
											item: styles['order-details__item'],
											image: styles['order-details__item-image'],
										}}
									/>
								);
							}
						})}
				</div>
				<OrderDetailsSummary
					classNames={{
						summary: styles['order-details__summary'],
						summaryTotal: styles['order-details__summary__total'],
					}}
					shippingCost={shippingCost}
					orderTotal={orderTotal}
					productTotal={productTotal}
					currency={currency}
				/>
				<OrderDetailsBottomSection
					classNames={styles['order-details__bottom-section']}
					shippingAddress={shippingAddress}
					billingAddress={billingAddress}
					paymentInstruments={paymentInstruments}
					currency={currency}
				/>
			</div>
		);
	};

	if (!isAccordion) {
		return renderOrderDetailsBody();
	}

	return (
		<Accordion
			key={order.getOrderNumber()}
			classNames={{accordion: cn(styles['order-details__accordion'], 'order-details__accordion')}}
			titleContent={
				<OrderDetailsAccordionHeading
					creationDate={order.getOrderCreationDate()}
					status={order.getStatusToDisplay()}
					orderNumber={orderNumber}
					returnAuthorizationStatusLabel={returnAuthorizationStatusLabel}
				/>
			}
		>
			{renderOrderDetailsBody()}
		</Accordion>
	);
};

export default OrderDetails;
